import { WS_SERVER_ADDRESS } from '../Constants/serverAddress';
import { getAccessToken } from './localStorageServices';

const WS_URL = WS_SERVER_ADDRESS;
export const NOTIFICATION_COUNT_STORAGE_KEY = 'ws-notification-unread-count';

let webSocket = null;

// Custom event name for notifications
export const NOTIFICATION_EVENT = 'ws-notification';

const getStoredUnreadCount = () => {
    const storedUnreadCount = Number(localStorage.getItem(NOTIFICATION_COUNT_STORAGE_KEY) || 0);
    return Number.isFinite(storedUnreadCount) ? storedUnreadCount : 0;
};

/**
 * Dispatch a custom event when notification is received
 * @param {string} title 
 * @param {string} description 
 */
const dispatchNotificationEvent = (title, description, unreadCount, silent = false) => {
    const normalizedUnreadCount = Number.isFinite(Number(unreadCount)) ? Number(unreadCount) : 0;
    localStorage.setItem(NOTIFICATION_COUNT_STORAGE_KEY, `${normalizedUnreadCount}`);
    const event = new CustomEvent(NOTIFICATION_EVENT, {
        detail: { title, description, unreadCount: normalizedUnreadCount, silent }
    });
    window.dispatchEvent(event);
};

export const syncUnreadNotificationCount = (unreadCount) => {
    dispatchNotificationEvent('', '', unreadCount, true);
};

/**
 * Initialize WebSocket connection with access token
 * @param {string} accessToken - The access token to use for authentication
 */
export const initializeWebSocket = (accessToken) => {
    // Return if already connecting or connected
    if (webSocket && (webSocket.readyState === WebSocket.OPEN || webSocket.readyState === WebSocket.CONNECTING)) {
        return;
    }

    // Close existing connection if any
    if (webSocket) {
        closeWebSocket();
    }

    const token = accessToken || getAccessToken();
    if (!token || token === 'undefined') {
        console.warn('No valid access token available for WebSocket connection');
        return;
    }

    const wsUrl = `${WS_URL}?token=${token}`;

    try {
        webSocket = new WebSocket(wsUrl);

        webSocket.onopen = () => {
            console.log('WebSocket connection established');
        };

        webSocket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                // Handle notification message format: { message: { title, description }, type: "user_notification" }
                if (data.type === 'user_notification' && data.message) {
                    const { title, description, user_wise_unread_notification_count } = data.message;
                    const currentUserEmail = JSON.parse(localStorage.getItem("user-details"))?.email;
                    const previousUnreadCount = getStoredUnreadCount();
                    const serverUnreadCount = Number(user_wise_unread_notification_count?.[currentUserEmail]);
                    const unreadCount = Number.isFinite(serverUnreadCount)
                        ? serverUnreadCount > previousUnreadCount
                            ? serverUnreadCount
                            : previousUnreadCount + 1
                        : previousUnreadCount + 1;
                    dispatchNotificationEvent(title, description, unreadCount);
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        webSocket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        webSocket.onclose = (event) => {
            console.log('WebSocket connection closed:', event.code, event.reason);
            webSocket = null;
        };

    } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
    }
};

/**
 * Close the WebSocket connection
 */
export const closeWebSocket = () => {
    if (webSocket) {
        webSocket.onclose = null; // Prevent onclose from firing and messing up state
        webSocket.close();
        webSocket = null;
        console.log('WebSocket connection closed by client');
    }
};

/**
 * Reconnect WebSocket with a new access token (used after token refresh)
 */
export const reconnectWebSocket = (accessToken) => {
    const newToken = accessToken || getAccessToken();
    if (newToken) {
        console.log('Reconnecting WebSocket with new token');
        initializeWebSocket(newToken);
    }
};

/**
 * Check if WebSocket is currently connected
 * @returns {boolean}
 */
export const isWebSocketConnected = () => {
    return webSocket !== null && webSocket.readyState === WebSocket.OPEN;
};

// End of file
